import React from 'react';
// import { useEffect } from 'react';
import PropTypes from 'prop-types';
import { withRouter, useLocation } from 'react-router-dom';

const _ = {
    get: require('lodash/get'),
    once: require('lodash/once'),
    forEach: require('lodash/forEach'),
};

const Globals = require('../../app/globals');
import Navigator from './Navigator';
const Strings = require('../../app/shared/Strings');
const SignInActions = require('./actions/sign_in_actions');
const SignInStore = require('./stores/SignInStore');
const ViewStateStore = require('./stores/ViewStateStore');
const SupportUtil = require('./util/support');
import withSignInStoreState from './components/DataHOCs/WithSignInStoreState';
const UriUtil = require('./util/UriUtil');
import NotFound from './components/NotFound';

// path constants (for those repeated in this file)
const SIGN_IN_PATH = '/signin';
const DASHBOARD_HOME_PATH = Navigator.paths.dashboard;

// Events which might affect vertical height
const vhChangeEventTypes = [
    "resize",
    "fullscreenchange",
    "orientationchange"
];

var is_ios = _.once(function() {
    return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod'
    ].includes(navigator.platform)
    // iPad on iOS 13 detection
    || (navigator.userAgent.includes("Mac") && "ontouchend" in document)
});

// needed because 100vh does not leave room for the ios toolbar
const adjust_custom_vh = ()=>{
    // References:
    //    https://css-tricks.com/the-trick-to-viewport-units-on-mobile/
    //    https://aryedoveidelman.com/fixing_vh_units_on_mobile_once_and_for_all
    var screenHeight = window.innerHeight;

    var vh = (screenHeight / 100) + "px";
    if (is_ios()) {
        var fullscreenElement = document.fullscreenElement || document.mozFullScreenElement || document.webkitFullscreenElement || document.msFullscreenElement;
        if (!fullscreenElement) {
            vh = ((screenHeight - 44)/ 100) + "px"; // hack because iOS insists on putting its toolbar over the top of the page 
        }
    }

    //console.log("screenHeight adjusted");
    
    document.documentElement.style.setProperty('--custom-vh', vh); // set our own variable instead of using vh directly
};

// Root component =============================================================
function AppRootAlt(props) {
    const [viewName, setViewName] = React.useState(ViewStateStore.getState().viewName);
    ViewStateStore.listen((newStoreState) => setViewName(newStoreState.viewName));

    _.forEach(vhChangeEventTypes,(type)=>{
        React.useEffect(() => {
            window.addEventListener(type, adjust_custom_vh);

            adjust_custom_vh();

            return () => window.removeEventListener(type, adjust_custom_vh);
        }, []);
    });

    return (
        <div id="app-root">
            <div
                id="aria-live-region"
                className="sr-only"
                aria-live="polite"
                aria-atomic="true"
            >
                {(!!viewName) &&
                    <span>Navigated to {viewName}</span>
                }
            </div>
            <div className={props.rootClassName}>{props.children}</div>
        </div>
    );
}

AppRootAlt.propTypes = {
    children: PropTypes.node,
    rootClassName: PropTypes.string,
};
AppRootAlt.defaultProps = { rootClassName: "mlAuthor" };


class AppRootBase extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            viewName: ViewStateStore.getState().viewName
        };

        this.unsubscribes = [ViewStateStore.listen(this.onViewStateStoreChange.bind(this))];
    }

    componentWillUnmount() {
        this.unsubscribes.forEach(u => u());
    }

    onViewStateStoreChange(newState) {
        const newViewName = ViewStateStore.getState().viewName;
        // from: http://almerosteyn.com/2017/03/accessible-react-navigation
        if (newViewName) {
            setTimeout(() => {
                this.setState({ viewName: newViewName })
            }, 50);
            setTimeout(() => {
                this.setState({ viewName: '' })
            }, 500);
        }
    }

    render() {
        return (
            <div id="app-root">
                <div
                    id="aria-live-region"
                    className="sr-only"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    {(!!this.state.viewName) &&
                        <span>Navigated to {this.state.viewName}</span>
                    }
                </div>
                <div className={this.props.rootClassName}>{this.props.children}</div>
            </div>
        );
    }
}

AppRootBase.propTypes = {
    children: PropTypes.node,
    rootClassName: PropTypes.string,
    history: PropTypes.shape({
        location: PropTypes.shape({
            pathname: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
};
AppRootBase.defaultProps = { rootClassName: "mlAuthor "};

// const AppRoot = withRouter(AppRootBase);
const AppRoot = AppRootAlt;
export { AppRoot };
// ============================================================================

export function Page(props) {
    React.useEffect(() => {
        document.title = props.title || Strings['en'].page_title.DEFAULT;
    }, [props.title]);

    if (!!window.gtag && !!GlobalConfig.GaPropertyId) {
        let location = useLocation();
        React.useEffect(() => {
            const fullPath = UriUtil.getFullPathFromLocation(location);
            const pageTitle = props.title || document.title || Strings['en'].page_title.DEFAULT;
            console.debug('Send location.pathname:', fullPath, pageTitle);
            window.gtag('config', GlobalConfig.GaPropertyId, { page_path: fullPath, page_title: pageTitle });
            // window.gtag.send(["pageview", fullPath]);
        }, [location]);
    }

    return props.children;
}

Page.propTypes = { title: PropTypes.string };
Page.defaultProps = { title: '' };

// Second level components ====================================================

// no additional functionality
export function SignedOutOrSignedIn(props) {
    return <Page title={props.title}>{props.children}</Page>;
}

// for routes that should transition directly to the default signed in view
// ...if session cookie (LOCAL) exists
class AuthorSignedOutBase extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
        this.shouldTransition = this.shouldTransition.bind(this);
        this.transition = this.transition.bind(this);
    }

    componentDidMount() {
        if (this.shouldTransition()) this.transition();
    }

    componentDidUpdate() {
        if (this.shouldTransition()) this.transition();
    }

    shouldTransition() {
        return !!this.props.signInStoreState.initializedLocal;
    }

    transition() {
        const query = UriUtil.parseLocationSearchString(this.props.location);
        // redirect to pre-set location
        const redirectTo = query[Globals.uri.query.REDIRECT_TO] || null;
        // redirect to custom path (superceded by redirectTo)
        const redirectToPath = query[Globals.uri.query.REDIRECT_TO_PATH] || null;
         // additional query param for redirection to assignment
        const hash = query.hash || null;
         // additional query param for redirection to assignment
        const suffix = query.suffix || null;

        if (!!query.host_url && query.host_url === 'support.muzzylane.com') {
            // the user will have just logged out of the support site.
            // can insert custom logic here if desired
        }

        switch (redirectTo) {
            case 'support': {
                window.location.href = SupportUtil.getSupportUrl();
                break;
            }
            case 'assignment': {
                window.location.href = `/assignment/${hash}/${suffix}`;
                break;
            }
            case 'course_instance': {
                window.location.href = `/course_instance/${hash}/authorize`;
                break;
            }
            case 'course_instance_direct': {
                window.location.href = `/course_instance/${hash}`;
                break;
            }
            default: {
                if (redirectToPath) {
                    this.props.history.replace(redirectToPath);
                }
                else {
                    this.props.history.replace(DASHBOARD_HOME_PATH);
                }
            }
        }
    }

    render() {
        return <Page title={this.props.title}>{this.props.children}</Page>;
    }
}

AuthorSignedOutBase.propTypes = {
    history: PropTypes.object.isRequired,
    location: PropTypes.shape({
        search: PropTypes.string.isRequired,
    }).isRequired,
    signInStoreState: PropTypes.object.isRequired,
};
AuthorSignedOutBase.defaultProps = {};

const AuthorSignedOut = withRouter(withSignInStoreState(AuthorSignedOutBase));
export { AuthorSignedOut };

// for routes requiring user to have existing LOCAL session cookie
// ...if not present, transition to /signin path
const keepAliveInterval = 180000;
let keepAliveIntervalId = null;
class AuthorSignedInBase extends React.Component {
    constructor(props) {
        super(props);
        this.state = {};
        this.shouldTransition = this.shouldTransition.bind(this);
        this.shouldSetKeepAlive = this.shouldSetKeepAlive.bind(this);
        this.setKeepAlive = this.setKeepAlive.bind(this);
        this.transition = this.transition.bind(this);
    }

    componentDidMount() {
        if (this.shouldTransition(this.props.signInStoreState)) {
            this.transition();
        }

        if (this.shouldSetKeepAlive(this.props.signInStoreState)) {
            this.setKeepAlive();
        }
    }

    componentDidUpdate() {
        if (this.shouldTransition(this.props.signInStoreState)) {
            this.transition();
        }

        if (this.shouldSetKeepAlive(this.props.signInStoreState)) {
            this.setKeepAlive();
        }
    }

    componentWillUnmount() {
        if (keepAliveIntervalId) clearInterval(keepAliveIntervalId);
        keepAliveIntervalId = null;
    }

    shouldSetKeepAlive(signInStoreState) {
        return signInStoreState.initializedLocal && !!signInStoreState.userInfo.org_idp && !keepAliveIntervalId;
    }

    setKeepAlive() {
        if (!!keepAliveIntervalId) return;

        keepAliveIntervalId = setInterval(() => {
            SignInActions.keepAlive();
        }, keepAliveInterval);
    }

    shouldTransition(signInStoreState) {
        return !signInStoreState.pendingUserInfo && !signInStoreState.initializedLocal;
    }

    transition() {
        const path = UriUtil.getFullPathFromLocation(this.props.location);
        const redirectParam = !!path ? `?${Globals.uri.query.REDIRECT_TO_PATH}=${encodeURIComponent(path)}` : '';
        const idpInfo = _.get(this.props.signInStoreState, 'userInfo.org_idp', null);
        if (!idpInfo) {
            this.props.history.replace(SIGN_IN_PATH + redirectParam);
        }
        else {
            this.props.history.replace(`/authoring/${idpInfo.type}/login/${idpInfo.token}${redirectParam}`);
        }
    }

    render() {
        return <Page title={this.props.title}>{this.props.signInStoreState.initialized ? this.props.children : null}</Page>;
    }
}

AuthorSignedInBase.propTypes = {
    history: PropTypes.object.isRequired,
    location: PropTypes.shape({
        search: PropTypes.string.isRequired,
    }).isRequired,
    signInStoreState: PropTypes.shape({
        initializedLocal: PropTypes.bool.isRequired,
        userInfo: PropTypes.object,
        pendingUserInfo: PropTypes.bool.isRequired,
    }).isRequired,
}
AuthorSignedInBase.defaultProps = {};
const AuthorSignedIn = withRouter(withSignInStoreState(AuthorSignedInBase));
export { AuthorSignedIn };

// for routes requiring user to have existing LOCAL session cookie and admin privileges
class AdminAreaBase extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            deny: this.shouldDeny(props.signInStoreState)
        };
        this.shouldDeny = this.shouldDeny.bind(this);
        this.transition = this.transition.bind(this);
    }
    shouldDeny(state) {
        return !state.pendingUserInfo && (!state.initializedLocal || !state.userInfo.admin);
    }
    transition() {
        this.props.history.push(SIGN_IN_PATH);
    }
    render() {
        return <Page title={this.props.title}>{this.props.signInStoreState.initialized && !this.shouldDeny(this.props.signInStoreState) ? this.props.children : <NotFound />}</Page>;
    }
}

AdminAreaBase.propTypes = {
    history: PropTypes.object.isRequired,
    signInStoreState: PropTypes.shape({
        initializedLocal: PropTypes.bool.isRequired,
        userInfo: PropTypes.object,
        pendingUserInfo: PropTypes.bool.isRequired,
    }).isRequired,
};
AdminAreaBase.defaultProps = {};
const AdminArea = withRouter(withSignInStoreState(AdminAreaBase));
export { AdminArea };
// ============================================================================